import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { DateAdapter } from '@angular/material/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { readStorage, writeStorage } from '../utils/safe-storage';

export type AppLang = 'it' | 'en';

const STORAGE_KEY = 'app.lang';
const DEFAULT_LANG: AppLang = 'en';
const SUPPORTED: AppLang[] = ['it', 'en'];

const LOCALE_MAP: Record<AppLang, string> = {
  it: 'it-IT',
  en: 'en-US',
};

export const APP_LANG_STORAGE_KEY = STORAGE_KEY;

export function resolveInitialAppLang(): AppLang {
  return AppLocaleService.readInitial();
}

export function resolveInitialAngularLocale(): string {
  return LOCALE_MAP[AppLocaleService.readInitial()];
}

@Injectable({ providedIn: 'root' })
export class AppLocaleService {

  private readonly subject = new BehaviorSubject<AppLang>(AppLocaleService.readInitial());

  readonly current$: Observable<AppLang> = this.subject.asObservable();

  constructor(private translate: TranslateService, private dateAdapter: DateAdapter<unknown>) {
    window.addEventListener('storage', this.onStorageEvent);
  }

  init(): void {
    const initial = this.subject.value;
    this.translate.addLangs(SUPPORTED);
    this.translate.setDefaultLang(DEFAULT_LANG);
    this.translate.use(initial);
    this.dateAdapter.setLocale(LOCALE_MAP[initial]);
    AppLocaleService.stampDocumentLang(initial);
  }

  get current(): AppLang {
    return this.subject.value;
  }

  get locale(): string {
    return LOCALE_MAP[this.subject.value];
  }

  /**
   * Switch language. The write is guarded: a refused `setItem` costs only the persistence of the
   * choice (it reverts to the default on the next boot), never the switch itself -- an unguarded
   * throw here surfaced through the language-switcher click handler into the global ErrorHandler.
   */
  setLanguage(lang: AppLang): void {
    if (!SUPPORTED.includes(lang)) return;
    if (lang === this.subject.value) return;
    writeStorage('local', STORAGE_KEY, lang);
    this.applyLanguage(lang);
  }

  private applyLanguage(lang: AppLang): void {
    if (!SUPPORTED.includes(lang)) return;
    if (lang === this.subject.value) return;
    this.translate.use(lang);
    this.dateAdapter.setLocale(LOCALE_MAP[lang]);
    AppLocaleService.stampDocumentLang(lang);
    this.subject.next(lang);
  }

  // Chiama applyLanguage (non setLanguage) per non riscrivere localStorage: riscriverlo causerebbe un loop di storage-event tra tab.
  private readonly onStorageEvent = (event: StorageEvent): void => {
    if (event.storageArea !== localStorage) return;
    if (event.key !== STORAGE_KEY) return;
    if (event.newValue === null) return;
    this.applyLanguage(event.newValue as AppLang);
  };

  /**
   * WCAG 3.1.1 (Language of Page), level A. `src/index.html` ships a STATIC `lang="en"` and nothing
   * ever moved it, so every user who picked Italian was served Italian text under an English
   * declaration (the fallback language is `en`, which is why the static value matched it). A screen
   * reader picks its pronunciation rules from this attribute: `it` copy read as `en` comes out as
   * English phonetics applied to Italian words, which is not a degraded reading but an
   * unintelligible one. It also drives hyphenation, and what a translation tool offers to
   * translate.
   *
   * Stamped from BOTH places that settle a language -- `init()` at boot and `applyLanguage()` for a
   * user's pick AND the cross-tab `storage` event -- because a value set in one of the two is a
   * value that goes stale the first time the other one runs. The tag comes from LOCALE_MAP rather
   * than the bare subtag, so there is no second table to disagree with the one the date adapter
   * already uses.
   *
   * RESIDUE, stated rather than hidden: this runs when the BUNDLE runs, so a cold load declares
   * `en` for the moments before boot. The theme, contrast and colour-vision presets each solve
   * their equivalent with an inline boot stanza in `src/index.html`; one is not added here because
   * those three exist to stop a visible FLASH of the wrong paint, while assistive tech reads the
   * attribute after load -- and a fourth stanza is real risk against that much smaller prize (the
   * theme stanza `return`s on its hot path, which is why they are separate IIFEs at all).
   */
  static stampDocumentLang(lang: AppLang): void {
    document.documentElement.lang = LOCALE_MAP[lang];
  }

  static readInitial(): AppLang {
    const stored = readStorage('local', STORAGE_KEY) as AppLang | null;
    if (stored && (SUPPORTED as string[]).includes(stored)) return stored;
    return DEFAULT_LANG;
  }
}
